import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { ApiResponse } from '../models/api-response';
import { SubdomainCheckDto, TenantSignupRequestDto } from '../models/tenant-signup';
import { TenantModule, TenantType } from './tenant-features.service';

/**
 * Admin (super-admin / Administrator only) access to public tenant-signup requests.
 * Backed by TenantSignupRequestsController, which is restricted to the Administrator role.
 */
@Injectable({ providedIn: 'root' })
export class TenantSignupService {
  private baseUrl = `${environment.apiUrl}/TenantSignupRequests`;

  constructor(private http: HttpClient) {}

  list(
    pageNumber = 1,
    pageSize = 20,
    status?: string
  ): Observable<ApiResponse<TenantSignupRequestDto[]>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize);
    if (status) params = params.set('status', status);

    return this.http.get<ApiResponse<TenantSignupRequestDto[]>>(`${this.baseUrl}/List`, { params });
  }

  /**
   * Approve / reject a request. On approval, `tenantType` and `modules` override the defaults the
   * server derives from the request's organization type (omit them to accept the defaults).
   */
  updateStatus(
    id: string,
    status: string,
    subdomain?: string,
    tenantType?: TenantType,
    modules?: TenantModule[]
  ): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.baseUrl}/UpdateStatus/${id}`, {
      status,
      subdomain,
      tenantType,
      modules,
    });
  }

  /** Live-check whether a subdomain is valid and free. */
  checkSubdomain(subdomain: string): Observable<ApiResponse<SubdomainCheckDto>> {
    const params = new HttpParams().set('subdomain', subdomain);
    return this.http.get<ApiResponse<SubdomainCheckDto>>(`${this.baseUrl}/CheckSubdomain`, { params });
  }

  /** Re-send the set-password link to an already-provisioned tenant's admin. */
  resend(id: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${this.baseUrl}/Resend/${id}`, {});
  }

  /** Delete a signup-request lead record (does not affect a provisioned tenant). */
  delete(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.baseUrl}/Delete/${id}`);
  }
}
